import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generic axios instance for direct API calls (api.get, api.post, etc.)
const axiosInstance = axios.create({
  baseURL: API,
});

// Attach auth token + the user's local IANA timezone to every request, so the
// backend rolls days over at LOCAL midnight (check-ins, streaks, Daily Read).
function localTz() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
}
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('hpme_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const tz = localTz();
  if (tz) config.params = { ...(config.params || {}), tz };
  return config;
});

// Named methods for backward compatibility with existing pages
axiosInstance.getWorkouts = () => axiosInstance.get('/workouts');
axiosInstance.getWorkout = (id) => axiosInstance.get(`/workouts/${id}`);
axiosInstance.createWorkout = (data) => axiosInstance.post('/workouts', data);
axiosInstance.deleteWorkout = (id) => axiosInstance.delete(`/workouts/${id}`);
axiosInstance.getExerciseDefinitions = (workoutId) => axiosInstance.get(`/exercise-definitions/${workoutId}`);
axiosInstance.getExerciseDefinition = (name) => axiosInstance.get(`/exercise-definition/${encodeURIComponent(name)}`);
axiosInstance.startSession = (workoutId) => axiosInstance.post(`/sessions/start?workout_id=${workoutId}`);
axiosInstance.completeSession = (sessionId) => axiosInstance.post(`/sessions/${sessionId}/complete`);
axiosInstance.getSessions = () => axiosInstance.get('/sessions');
axiosInstance.getSession = (id) => axiosInstance.get(`/sessions/${id}`);
axiosInstance.deleteSession = (id) => axiosInstance.delete(`/sessions/${id}`);
axiosInstance.getFatigueAnalysis = () => axiosInstance.get('/analysis/fatigue');
axiosInstance.meditationDefaults = () => axiosInstance.get('/meditations/defaults');
axiosInstance.meditationPrepare = (payload) => axiosInstance.post('/meditations/prepare', payload);
axiosInstance.completeMeditation = (payload) => axiosInstance.post('/meditations/complete', payload);
axiosInstance.getMentalSessions = (limit = 20) => axiosInstance.get('/wellness/mental-sessions', { params: { limit } });
axiosInstance.getJourney = () => axiosInstance.get('/wellness/journey');
axiosInstance.getBadges = () => axiosInstance.get('/wellness/badges');
axiosInstance.getInjuryRisk = () => axiosInstance.get('/analysis/injury-risk');
axiosInstance.getRecoveryAnalysis = () => axiosInstance.get('/analysis/recovery');
axiosInstance.getProgressionOverview = () => axiosInstance.get('/progression/overview');
axiosInstance.getPerformanceMetrics = () => axiosInstance.get('/performance/metrics');
axiosInstance.getPerformanceProgression = () => axiosInstance.get('/performance/progression');
axiosInstance.getPerformanceSummary = () => axiosInstance.get('/performance/summary');
axiosInstance.syncWearable = (source) => axiosInstance.post(`/wearables/sync?source=${source}`);
axiosInstance.getLatestWearable = () => axiosInstance.get('/wearables/latest');
axiosInstance.getWearableHistory = () => axiosInstance.get('/wearables/history');
axiosInstance.getCoachWorkouts = () => axiosInstance.get('/coach/workouts');
axiosInstance.getCoachStats = () => axiosInstance.get('/coach/stats');
axiosInstance.getRecoveryBodyMap = () => axiosInstance.get('/recovery/body-map');
// NOTE: /vitals POST/GET endpoints kept on the backend for future wearable
// sync, but the manual "Log Vitals" UI was removed in iter67 (sleep already
// lives in the unified daily check-in). Re-add helpers here when wearables
// land — don't reintroduce a manual form.

// Mobility & ROM Baseline — camera-driven, bilateral
axiosInstance.getMovementBaselineCatalog = () => axiosInstance.get('/movement-baseline/catalog');
axiosInstance.getCurrentBaseline = () => axiosInstance.get('/movement-baseline/current');
axiosInstance.startBaselineAssessment = () => axiosInstance.post('/movement-baseline/start');
// New shape: { test_id, measurements: [{side, raw_value, guard_violated, guard_peak_violation}] }
axiosInstance.submitBaselineTest = (test_id, measurements) =>
  axiosInstance.post('/movement-baseline/submit-test', { test_id, measurements });
axiosInstance.retestBaseline = () => axiosInstance.post('/movement-baseline/retest');
axiosInstance.getBaselineHistory = () => axiosInstance.get('/movement-baseline/history');
// Score a mobility measurement computed IN-BROWSER (no video upload).
axiosInstance.scoreMobilityMeasurement = (payload) => axiosInstance.post('/movement-baseline/score-measurement', payload);
// Nikki's Daily Read — the visible surface of her COM-B behaviour engine.
axiosInstance.getDailyRead = () => axiosInstance.get('/wellness/daily-read');

// Video Movement Analyser — client-side MediaPipe processing, server-side narration
axiosInstance.getMovementCatalog = () => axiosInstance.get('/video-analysis/catalog');
axiosInstance.interpretMovement = (payload) => axiosInstance.post('/video-analysis/interpret', payload);
axiosInstance.saveMovementAnalysis = (payload) => axiosInstance.post('/video-analysis/save', payload);
axiosInstance.getMovementAnalysisHistory = (limit = 25) =>
  axiosInstance.get('/video-analysis/history', { params: { limit } });
axiosInstance.getMovementAnalysis = (id) => axiosInstance.get(`/video-analysis/${id}`);
axiosInstance.uploadMovementVideo = (file, onProgress) => {
  const form = new FormData();
  form.append('file', file, file.name || 'clip.mp4');
  return axiosInstance.post('/video-analysis/upload-video', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onProgress,
    timeout: 5 * 60 * 1000,   // 5 minutes for slow uploads
  });
};
axiosInstance.deleteMovementAnalysis = (id) => axiosInstance.delete(`/video-analysis/${id}`);

// In-session Form Check (server-side pose) — score-only, stored on the session.
// Recommended camera angle(s) for a free-form exercise name.
axiosInstance.getExerciseGuidance = (name, movement_id) =>
  axiosInstance.get('/video-analysis/exercise-guidance', { params: { name, movement_id } });
// Save a CLIENT-SIDE form check (analyzed in-browser, same engine as live) onto the session.
axiosInstance.saveFormCheck = (payload) => axiosInstance.post('/video-analysis/form-check/save', payload);

// Wellness layer — Mental IQ, Wellness IQ composite, cycle tracking
axiosInstance.getWellnessIQ = () => axiosInstance.get('/wellness/iq');
axiosInstance.getDistrict = (pillar, tz) => axiosInstance.get(`/district/${pillar}`, { params: { tz } });
axiosInstance.upgradeDistrictBuilding = (pillar, building, tz) => axiosInstance.post(`/district/${pillar}/upgrade`, { building }, { params: { tz } });
axiosInstance.getAvatarComposite = () => axiosInstance.get('/avatar/composite');
axiosInstance.getAvatarStates = () => axiosInstance.get('/avatar/states');
axiosInstance.getAvatarStatus = () => axiosInstance.get('/avatar/status');
axiosInstance.generateAvatar = (formData) => axiosInstance.post('/avatar/generate', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
axiosInstance.regenerateAvatar = () => axiosInstance.post('/avatar/regenerate');
axiosInstance.regenerateAvatarBase = () => axiosInstance.post('/avatar/regenerate-base');
axiosInstance.confirmAvatarBase = () => axiosInstance.post('/avatar/confirm-base');
axiosInstance.getVillage = (pillar) => axiosInstance.get(`/village/${pillar}`);
axiosInstance.addVillageWisdom = (pillar, text) => axiosInstance.post(`/village/${pillar}/wisdom`, { text });
axiosInstance.heartVillageWisdom = (id) => axiosInstance.post(`/village/wisdom/${id}/heart`);
axiosInstance.getVillageOptin = () => axiosInstance.get('/village/settings/optin');
axiosInstance.setVillageOptin = (value) => axiosInstance.post('/village/settings/optin', { value });
axiosInstance.getTodayCheckin = () => axiosInstance.get('/wellness/checkin/today');
axiosInstance.getNutritionProfile = () => axiosInstance.get('/nutrition/profile');
axiosInstance.patchNutritionProfile = (payload) => axiosInstance.patch('/nutrition/profile', payload);
axiosInstance.getNutritionToday = () => axiosInstance.get('/nutrition/today');
axiosInstance.nutritionIntake = (kind, delta) => axiosInstance.post('/nutrition/intake', { kind, delta });
axiosInstance.submitCheckin = (payload) => axiosInstance.post('/wellness/checkin', payload);
axiosInstance.submitMoodCheckin = (payload) => axiosInstance.post('/wellness/checkin/mood', payload);
axiosInstance.getCheckinHistory = (days = 14) => axiosInstance.get('/wellness/checkin/history', { params: { days } });
axiosInstance.getBehaviorCheckin = () => axiosInstance.get('/wellness/behavior');
axiosInstance.reportBarrier = (payload) => axiosInstance.post('/wellness/barrier', payload);
axiosInstance.getCelebrations = () => axiosInstance.get('/wellness/celebrations');
axiosInstance.getBreathworkProtocols = () => axiosInstance.get('/wellness/breathwork/protocols');
axiosInstance.logBreathwork = (payload) => axiosInstance.post('/wellness/breathwork/complete', payload);
axiosInstance.soundscapeComplete = (payload) => axiosInstance.post('/soundscapes/complete', payload);
axiosInstance.getBreathworkStreak = () => axiosInstance.get('/wellness/breathwork/streak');
axiosInstance.setupCycleTracking = (payload) => axiosInstance.post('/wellness/cycle/setup', payload);
axiosInstance.getCycleToday = () => axiosInstance.get('/wellness/cycle/today');

// Nikki memory layer — facts she remembers about you
axiosInstance.getMemoryFacts = () => axiosInstance.get('/wellness/memory/facts');
axiosInstance.deleteMemoryFact = (fact_id) => axiosInstance.delete(`/wellness/memory/facts/${fact_id}`);


export const api = axiosInstance;
export default api;
